<template>
  <div
    ref="searchContainer"
    class="relative"
  >
    <div
      ref="searchInputWrapper"
      class="relative flex items-center"
      :class="containerClass"
    >
      <UIIcon
        name="search"
        class="absolute start-3 text-black-60 z-10"
      />
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="t('portfolio.portfolioSearch.placeholder')"
        autocomplete="off"
        class="w-full ps-10 pe-10 py-2 text-sm border border-black-20 rounded-lg outline-none focus:border-black-100 transition-colors bg-white"
        :class="inputClass"
        @focus="handleFocus"
        @input="handleInput"
        @blur="handleBlur"
      />
      <UIBaseTransition>
        <UIIcon
          v-if="searchQuery"
          name="big-close"
          class="absolute end-3 text-black-60 cursor-pointer z-10"
          @click="handleClear"
        />
      </UIBaseTransition>
      <UIBaseTransition>
        <div
          v-if="isSearching"
          class="absolute end-3 z-10"
        >
          <UIProgressSpinner
            width="16"
            height="16"
          />
        </div>
      </UIBaseTransition>
    </div>

    <UIBaseTransition>
      <div
        v-if="showDropdown && !hideDropdown"
        ref="dropdown"
        class="absolute z-[100] w-full mt-1 bg-white border border-black-20 rounded-lg shadow-lg max-h-96 overflow-y-auto"
        :style="dropdownStyles"
      >
        <div
          v-if="isSearching && searchResults.length === 0"
          class="p-4 text-sm text-black-60 text-center"
        >
          {{ t('portfolio.portfolioSearch.searching') }}
        </div>
        <div
          v-else-if="!isSearching && searchResults.length === 0 && searchQuery.trim()"
          class="p-4 text-sm text-black-60 text-center"
        >
          {{ t('portfolio.portfolioSearch.noResults') }}
        </div>
        <div
          v-else
          class="py-2"
        >
          <div
            v-if="hasResults"
            class="px-4 py-2"
          >
            <h3 class="text-sm font-bold text-black-100">{{ t('property.crumb.projects') }}</h3>
          </div>
          <button
            v-for="property in searchResults"
            :key="property.id || property.complex_id"
            class="w-full px-4 py-3 text-start transition-colors flex items-start gap-3 hover:bg-[#FFF0DA] group"
            @click="handleSelectProperty(property)"
            @mouseenter="hoveredProperty = property.id || property.complex_id"
            @mouseleave="hoveredProperty = null"
            :class="{ 'bg-[#FFF0DA]': hoveredProperty === (property.id || property.complex_id) }"
          >
            <div class="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-black-05">
              <NuxtImg
                v-if="property.photo"
                :src="property.photo"
                :alt="property.title"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-black-05 flex items-center justify-center"
              >
                <UIIcon
                  name="home"
                  class="text-black-40"
                />
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-black-100 truncate">
                {{ property.title }}
              </p>
              <p
                v-if="property.address"
                class="text-xs font-normal text-black-100 truncate mt-0.5"
              >
                {{ property.address }}
              </p>
            </div>
          </button>
        </div>
      </div>
    </UIBaseTransition>
  </div>
</template>

<script setup lang="ts">
import { useFloating, autoUpdate, flip, offset } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';

interface Props {
  containerClass?: string;
  inputClass?: string;
  hideDropdown?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  containerClass: '',
  inputClass: '',
  hideDropdown: false,
});

const { t } = useI18n();
const { searchQuery, searchResults, isSearching, showDropdown, performSearch, clearSearch } = usePortfolioSearch();

const searchContainer = ref<HTMLDivElement | null>(null);
const searchInputWrapper = ref<HTMLDivElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const dropdown = ref<HTMLDivElement | null>(null);

const hoveredProperty = ref<string | null>(null);

const hasResults = computed(() => searchResults.value.length > 0);

watch(searchQuery, (newQuery) => {
  if (newQuery.trim()) {
    performSearch(newQuery);
  } else {
    clearSearch();
  }
});

const handleFocus = () => {
  if (searchQuery.value.trim()) {
    performSearch(searchQuery.value);
  }
};

const handleInput = () => {};

const handleBlur = () => {
  if (props.hideDropdown) {
    return;
  }
  setTimeout(() => {
    if (!dropdown.value?.matches(':hover')) {
      clearSearch();
    }
  }, 200);
};

const handleClear = () => {
  clearSearch();
  searchInput.value?.focus();
};

const handleSelectProperty = (property: any) => {
  const propertyId = property.id || property.complex_id;
  navigateTo(`/property/${propertyId}`);
  clearSearch();
};

const { floatingStyles } = useFloating(searchInputWrapper, dropdown, {
  placement: 'bottom-start',
  strategy: 'absolute',
  whileElementsMounted: autoUpdate,
  middleware: [
    flip({
      fallbackPlacements: ['top-start'],
    }),
    offset(4),
  ],
});

const dropdownStyles = computed(() => ({
  ...floatingStyles.value,
  width: `${searchInputWrapper.value?.offsetWidth || 300}px`,
}));

onClickOutside(
  searchContainer,
  () => {
    if (!props.hideDropdown) {
      clearSearch();
    }
  },
  { ignore: [searchInput] },
);
</script>

<style scoped lang="scss"></style>
